/* eslint-disable max-len */
import type { FC } from "react";
import { useMemo } from "react";

import { renderToString } from "react-dom/server";

import { DonutChart } from "@similarweb/pro-logic-components";
import { colorsSets } from "@similarweb/styles";
import { LegendWithSubValueBullet } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { useBacklinksSummaryApi } from "app/pages/digital-marketing/backlinks/shared/backlinks-api-service";
import { useExpectedBacklinksParams } from "app/pages/digital-marketing/backlinks/shared/BacklinksUtils";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { ChartWithLegend } from "app/pages/digital-marketing/keywords-campaigns/reports/serp-feature-report/composition-charts/pie-chart/SERPReportCompositionPieChartStyledComponents";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import type { Point } from "highcharts";

const maxPresentedItems = 5;
const maxPresentedItemsSliceIndex = maxPresentedItems - 1;
const colors = colorsSets.c.toArray();

const GraphTooltipContent: FC<{ data: Point; headers?: string[] }> = ({ data, headers }) => {
    return (
        <Flex
            flexDirection="column"
            borderRadius="4px"
            gap="4px"
            background="white"
            width="200px"
            data-automation="tld-tooltip"
        >
            <Flex justifyContent="space-between">
                {headers?.map((header) => {
                    return (
                        <Text
                            fontVariant="caption"
                            key={header}
                        >
                            {header}
                        </Text>
                    );
                })}
            </Flex>
            <Flex justifyContent="space-between">
                <LegendWithSubValueBullet
                    text={data.name}
                    labelColor={data.color as string}
                />
                <Text fontVariant="caption">{data.percentage?.toFixed(2)}%</Text>
            </Flex>
        </Flex>
    );
};

export const BacklinksTLDsWidget: FC = () => {
    const translate = useTranslation();
    const params = useExpectedBacklinksParams();
    const { data, isLoading, isFetching } = useBacklinksSummaryApi({
        key: params.key ?? params.pageFilterParsed?.url,
        status: params.status ?? "All",
    });

    const tooltipHeaders = [
        translate("digitalsuite.backlinks.overview.tld_distribution.tooltip.tld"),
        translate("digitalsuite.backlinks.overview.tld_distribution.tooltip.refdomains"),
    ];

    const chartData = useMemo(() => {
        const { TopTlds } = data ?? {};
        if (!TopTlds) {
            return [];
        }

        const totalCount = TopTlds.reduce((acc, tldData) => acc + (tldData.Count ?? 0), 0);

        const returnedData = TopTlds.slice(0, maxPresentedItemsSliceIndex).map((tldData, index) => {
            const name = `.${tldData.Tld}`;
            const percentage = (tldData.Count / totalCount) * 100;

            return {
                name,
                id: name,
                y: tldData.Count / totalCount,
                total: tldData.Count / totalCount,
                visible: true,
                percentage: percentage.toFixed(2),
                color: colors[index],
            };
        });

        if (TopTlds.length > maxPresentedItems) {
            const othersCount = TopTlds.slice(maxPresentedItemsSliceIndex).reduce(
                (acc, tldData) => acc + (tldData.Count ?? 0),
                0,
            );
            const othersPercent = (othersCount / totalCount) * 100;
            returnedData.push({
                name: "Others",
                id: "Others",
                y: othersCount / totalCount,
                total: othersCount / totalCount,
                visible: true,
                percentage: othersPercent.toFixed(2),
                color: colors[colors.length - 1],
            });
        }

        return returnedData;
    }, [data]);

    return (
        <WidgetContainer
            title={translate("digitalsuite.backlinks.overview.tld_distribution.title")}
            isLoading={isLoading || isFetching}
            tooltip={translate("digitalsuite.backlinks.overview.tld_distribution.tooltip")}
            withBorder={true}
            bodyMinHeight={232}
            automationId="tld-distribution-chart"
        >
            <ChartWithLegend
                flexDirection="column"
                data-automation="tld-donut-chart"
            >
                <DonutChart
                    data={[{ data: chartData }]}
                    config={{
                        tooltip: {
                            shadow: true,
                            formatter(this) {
                                return renderToString(
                                    <GraphTooltipContent
                                        data={this}
                                        headers={tooltipHeaders}
                                    />,
                                );
                            },
                        },
                        chart: {
                            height: 200,
                            width: 200,
                            backgroundColor: "transparent",
                        },
                    }}
                    innerSize="60%"
                    legendsGridDirection="row"
                    withoutTooltipsPadding={false}
                    labelsAreRotated={false}
                    shouldPresentLegend={true}
                    legendPosition="right"
                    legendsMinWidth={200}
                    shouldPresentValueAtLegend={true}
                    legendValueFilter={(value: number) => `${((value ?? 0) * 100).toFixed(2)}%`}
                />
            </ChartWithLegend>
        </WidgetContainer>
    );
};
